import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Copy, Plus, Replace, Rocket, Target } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const KEY = "rpopro_boost_used";
const readUsed = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied")).catch(() => {});

const FIT = {
  strong: { label: "Strong fit", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" },
  possible: { label: "Possible fit", cls: "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3C649]" },
  weak: { label: "Weak fit", cls: "border-zinc-600 bg-zinc-800/40 text-zinc-400" },
};

const useMatches = (bullets, resume, jobId) => {
  const [m, setM] = useState({});
  const sig = JSON.stringify([bullets, (resume?.experience || []).map((e) => [e.id, e.role, e.company])]);
  useEffect(() => {
    const exp = resume?.experience || [];
    if (!bullets?.length || exp.length < 1) return setM({});
    let live = true;
    const t = setTimeout(() => {
      api.post("/resume-builder/match-bullets", { bullets, job_id: jobId || null, experience: exp.map((e) => ({ id: e.id, role: e.role, company: e.company, bullets: e.bullets.filter((x) => x.trim()).slice(0, 6) })) })
        .then(({ data }) => live && setM(Object.fromEntries((data?.matches || []).filter((x) => exp.some((e) => e.id === x.experience_id)).map((x) => [x.bullet_index, x]))))
        .catch(() => live && setM({}));
    }, 600);
    return () => { live = false; clearTimeout(t); };
  }, [sig, jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  return m;
};

const Hint = ({ match, resume, onAdd, i }) => {
  const k = resume.experience.findIndex((e) => e.id === match.experience_id);
  if (k < 0) return null;
  const e = resume.experience[k], f = FIT[match.fit] || FIT.possible;
  return (
    <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2" data-testid={`boost-bullet-hint-${i}`}>
      <span className={cn("inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium", f.cls)} title={match.reason || ""} data-testid={`boost-bullet-fit-${i}`}>
        <Target className="h-3 w-3 shrink-0" /><span className="truncate">{f.label} · {e.role || "Role"}{e.company && ` · ${e.company}`}</span>
      </span>
      <Button size="sm" onClick={() => onAdd(k)} className="h-7 min-w-0 max-w-full rounded-full px-3 text-[11px]" data-testid={`boost-bullet-quick-add-${i}`}><Plus className="h-3.5 w-3.5 shrink-0" /><span className="truncate">Add to {e.company || e.role || "this job"}</span></Button>
      {match.reason && <span className="w-full text-[11px] text-zinc-500" data-testid={`boost-bullet-reason-${i}`}>{match.reason}</span>}
    </div>
  );
};

const AddTo = ({ resume, onAdd, i }) => {
  const jobs = resume?.experience || [];
  if (!jobs.length) return <Button size="sm" variant="ghost" disabled className="h-7 px-2 text-[11px]">Add a job first</Button>;
  if (jobs.length === 1) return <Button size="sm" variant="ghost" onClick={() => onAdd(0)} className="h-7 min-w-0 max-w-full px-2 text-[11px] text-[#F3C649]" data-testid={`boost-bullet-add-${i}`}><Plus className="h-3.5 w-3.5 shrink-0" /><span className="truncate">Add to {jobs[0].company || jobs[0].role || "my job"}</span></Button>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-[#F3C649]" data-testid={`boost-bullet-add-${i}`}><Plus className="h-3.5 w-3.5" />Add to…<ChevronDown className="h-3 w-3" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-[#27272A] bg-[#141417]">
        <DropdownMenuLabel className="text-[11px] text-zinc-500">Add this bullet to</DropdownMenuLabel>
        {jobs.map((e, k) => <DropdownMenuItem key={e.id} onClick={() => onAdd(k)} data-testid={`boost-bullet-add-${i}-to-${k}`}>{e.role || "Role"}{e.company && ` · ${e.company}`}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const BoostContent = ({ content, resume, onUpdate, source, jobId }) => {
  const [used, setUsed] = useState(readUsed);
  const matches = useMatches(content?.bullets, resume, jobId);
  const mark = (t, v = true) => setUsed((u) => { const n = { ...u, [t]: v }; if (!v) delete n[t]; localStorage.setItem(KEY, JSON.stringify(n)); return n; });
  if (!content || (!content.summary && !content.bullets?.length)) return null;
  const useSummary = () => {
    const old = resume.summary;
    onUpdate((cur) => ({ ...cur, summary: content.summary }));
    mark(content.summary);
    toast.success("Summary replaced", { action: { label: "Undo", onClick: () => { onUpdate((cur) => ({ ...cur, summary: old })); mark(content.summary, false); } } });
  };
  const addBullet = (b, k) => {
    const id = resume.experience[k].id;
    const at = (cur, fn) => ({ ...cur, experience: cur.experience.map((e) => (e.id === id ? { ...e, bullets: fn(e.bullets) } : e)) });
    onUpdate((cur) => at(cur, (bs) => [...bs.filter((x) => x.trim()), b]));
    mark(b);
    toast.success(`Added to ${resume.experience[k].company || resume.experience[k].role || "your job"}`, { action: { label: "Undo", onClick: () => { onUpdate((cur) => at(cur, (bs) => { const i = bs.lastIndexOf(b); const n = i < 0 ? bs : bs.filter((_, j) => j !== i); return n.length ? n : [""]; })); mark(b, false); } } });
  };
  return (
    <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-3" data-testid="boost-content">
      <div className="mb-2 flex items-center gap-2 text-sm text-zinc-100"><Rocket className="h-4 w-4 text-emerald-400" />Boost content for this job</div>
      <p className="mb-3 text-[11px] text-zinc-500">Ready-to-use lines{source ? ` from ${source}` : ""}. Drop them into your resume, then edit to make them yours.</p>
      {content.summary && (
        <div className={cn("mb-3 rounded-lg border bg-[#09090B] p-3", used[content.summary] ? "border-[#27272A] opacity-60" : "border-[#27272A]")} data-testid="boost-summary">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-400">Optimized summary</div>
          <p className="break-words text-sm text-zinc-200">{content.summary}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            <Button size="sm" variant="ghost" onClick={useSummary} disabled={!resume} className="h-7 px-2 text-[11px] text-[#F3C649]" data-testid="boost-summary-use">{used[content.summary] ? <Check className="h-3.5 w-3.5" /> : <Replace className="h-3.5 w-3.5" />}{used[content.summary] ? "Used · replace again" : "Use as my summary"}</Button>
            <Button size="sm" variant="ghost" onClick={() => copy(content.summary)} className="h-7 px-2 text-[11px]" data-testid="boost-summary-copy"><Copy className="h-3.5 w-3.5" />Copy</Button>
          </div>
        </div>
      )}
      {content.bullets?.length > 0 && (
        <div data-testid="boost-bullets">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-400">Optimized bullets · {content.bullets.length}</div>
          <ul className="space-y-1.5">
            {content.bullets.map((b, i) => (
              <li key={i} className={cn("rounded-lg border border-[#27272A] bg-[#09090B] p-2.5", used[b] && "opacity-60")} data-testid={`boost-bullet-${i}`}>
                <p className="break-words text-sm text-zinc-200">{used[b] && <Check className="mr-1 inline h-3.5 w-3.5 text-emerald-400" />}{b}</p>
                {matches[i] && !used[b] && <Hint match={matches[i]} resume={resume} i={i} onAdd={(k) => addBullet(b, k)} />}
                <div className="mt-1 flex flex-wrap justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => copy(b)} className="h-7 px-2 text-[11px]" data-testid={`boost-bullet-copy-${i}`}><Copy className="h-3.5 w-3.5" />Copy</Button>
                  <AddTo resume={resume} i={i} onAdd={(k) => addBullet(b, k)} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
